import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LOADER } from '@/constants/testIds';
import { prefersReducedMotion } from '@/lib/hooks';

/**
 * Refined LoadingScreen — light cream backdrop, animated hex assembly + amber rim.
 * Shorter (2.4s) and quieter for the editorial brand mood.
 */
export default function LoadingScreen({ onComplete, duration = 2400 }) {
  const [exiting, setExiting] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const reduced = prefersReducedMotion();
    const total = reduced ? 500 : duration;

    const startExit = setTimeout(() => triggerExit(), total);
    const fallback = setTimeout(() => triggerExit(), Math.max(total, 3500));
    const tick = setInterval(() => {
      setProgress((p) => Math.min(100, p + 100 / (total / 70)));
    }, 70);

    function triggerExit() {
      setExiting(true);
      setTimeout(() => onComplete && onComplete(), 480);
      clearTimeout(fallback);
      clearTimeout(startExit);
      clearInterval(tick);
    }
    return () => {
      clearTimeout(fallback);
      clearTimeout(startExit);
      clearInterval(tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const top = { initial: { y: 0 }, exit: { y: '-100%', transition: { duration: 0.45, ease: [0.76, 0, 0.24, 1] } } };
  const bottom = { initial: { y: 0 }, exit: { y: '100%', transition: { duration: 0.45, ease: [0.76, 0, 0.24, 1] } } };

  return (
    <div data-testid={LOADER.root} className="fixed inset-0 z-[100] pointer-events-none">
      <AnimatePresence>
        <motion.div key="top"
          className="absolute top-0 left-0 right-0 h-1/2 pointer-events-auto"
          style={{ background: '#FBFAF7' }}
          variants={top} initial="initial" animate={exiting ? 'exit' : 'initial'} />
        <motion.div key="bottom"
          className="absolute bottom-0 left-0 right-0 h-1/2 pointer-events-auto"
          style={{ background: '#FBFAF7' }}
          variants={bottom} initial="initial" animate={exiting ? 'exit' : 'initial'} />

        <motion.div
          key="content"
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 1, scale: 1 }}
          animate={exiting ? { opacity: 0, scale: 1.04, transition: { duration: 0.22 } } : { opacity: 1, scale: 1 }}
        >
          <div className="relative flex flex-col items-center">
            <motion.div
              className="relative"
              initial={{ rotate: 0 }}
              animate={{ rotate: [0, 0, 18] }}
              transition={{ duration: 2.4, times: [0, 0.7, 1], ease: 'easeInOut' }}
            >
              <svg width="120" height="120" viewBox="0 0 120 120">
                <motion.polygon
                  points="60,12 102,36 102,84 60,108 18,84 18,36"
                  fill="none"
                  stroke="#E89A1F"
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeDasharray="310"
                  initial={{ strokeDashoffset: 310 }}
                  animate={{ strokeDashoffset: 0 }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                />
                <motion.text
                  x="60" y="75" textAnchor="middle"
                  fontFamily="Syne, sans-serif" fontWeight="600" fontSize="46"
                  fill="#0F1A33"
                  initial={{ opacity: 0, scale: 0.7 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, duration: 0.4 }}
                  style={{ transformOrigin: '60px 60px' }}
                >
                  S
                </motion.text>
              </svg>
              {[{ s: 14, x: 102, y: 22 }, { s: 11, x: 116, y: 36 }, { s: 8, x: 112, y: 52 }].map((h, i) => (
                <motion.svg
                  key={i} width={h.s} height={h.s} viewBox="0 0 24 24"
                  style={{ position: 'absolute', left: h.x, top: h.y }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.9 + i * 0.1, type: 'spring', stiffness: 200, damping: 14 }}
                >
                  <polygon points="12,2 21,7 21,17 12,22 3,17 3,7" fill="#E89A1F" />
                </motion.svg>
              ))}
            </motion.div>

            <motion.div
              className="mt-7 font-display font-semibold text-2xl md:text-3xl text-[#0F1A33]"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.35 }}
            >
              sitehive<span className="text-[color:var(--sh-amber-2)]">.in</span>
            </motion.div>

            <motion.div
              className="mt-6 h-[2px] rounded-full overflow-hidden"
              style={{ width: '160px', background: 'rgba(232, 154, 31, 0.18)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: exiting ? 0 : 1 }}
              transition={{ delay: 1.2, duration: 0.3 }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${progress}%`,
                  background: '#E89A1F',
                  transition: 'width .15s linear',
                }}
              />
            </motion.div>

            <motion.p
              className="mt-4 font-accent"
              style={{ fontSize: '10px', letterSpacing: '0.25em', color: '#5A6B85' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.8 }}
              transition={{ delay: 1.4, duration: 0.4 }}
            >
              WEBSITES THAT BUILD YOUR BUSINESS
            </motion.p>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
